/**
 * BLRRD Persistence — Projects store.
 *
 * A Project = (file reference, edit-state snapshot, thumbnail). One file
 * can have multiple projects (e.g. "Cinematic edit" + "B&W variant"), but
 * the typical case is one project per file with auto-save.
 *
 *   projects: { id, schemaVersion, fileHash, name, updatedAt, state, thumbnail }
 *
 *   state = {
 *     adjust:         { ...slider values },
 *     grade:          { ...wheel values },
 *     curves:         { master, r, g, b },
 *     curveIntensity: { ... },
 *     layers:         [],
 *     activePreset:   string,
 *     cropApplied:    { x, y, w, h } | null,
 *     outputSpace:    'sRGB' | 'p3',
 *   }
 *
 * Codex Round 2 hardening:
 *   R2-4  All helpers resolve on `tx.oncomplete` (commit barrier), not
 *         `request.onsuccess` (queue-success only).
 *   Schema-version SoT: `record.schemaVersion` is the canonical field.
 *   `state.schemaVersion` is intentionally NOT written — one source of
 *   truth means one migration table.
 *
 * Codex Fix #5 — snapshot source-of-truth: NAMED SNAPSHOTS stay in
 * `localStorage.blrrd_snapshots` keyed by `E.imgKey` (legacy editor's
 * canonical store, reviewed in Phase 1.C). Projects do NOT mirror the
 * snapshot list — that would create two sources of truth and stale-merge
 * bugs.
 *
 * Pure data — no DOM, no closure over `E`. The legacy editor's auto-save
 * hook serializes its `E` slice through `serializeEditorState()` here.
 */

import { openDB } from './idb.js';

const STORE = 'projects';

/* Per Codex Fix #8 — version every persisted record so renames in the
 * `state` shape can migrate forward instead of breaking silently. */
export const PROJECT_SCHEMA_VERSION = 1;

/* ── helpers ───────────────────────────────────────────────────────── */

function awaitTx(tx, result) {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error || new Error('IDB transaction failed'));
        tx.onabort = () => reject(tx.error || new Error('IDB transaction aborted'));
    });
}

/* Tiny non-cryptographic UUID — Math.random is fine for project IDs since
 * collision risk in single-user storage is astronomically low and we don't
 * need cryptographic uniqueness. */
function uuid() {
    const r = () => Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, '0');
    return `${r()}-${r().slice(0, 4)}-${r().slice(0, 4)}-${r()}`;
}

/* ── public API ────────────────────────────────────────────────────── */

/**
 * Save a project. If `record.id` is set, updates; otherwise creates new.
 * Resolves only after the transaction commits (Codex R2-4).
 */
export async function saveProject(record) {
    if (!record || !record.fileHash) throw new Error('saveProject: missing fileHash');
    const db = await openDB();
    try {
        const stored = {
            id:            record.id || uuid(),
            schemaVersion: record.schemaVersion || PROJECT_SCHEMA_VERSION,
            fileHash:      record.fileHash,
            name:          record.name || 'Untitled',
            updatedAt:     Date.now(),
            state:         record.state || {},
            thumbnail:     record.thumbnail || null,
        };
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(stored);
        return await awaitTx(tx, stored.id);
    } finally {
        db.close();
    }
}

/** Load a project by id, or null. Migrates old records on read if needed. */
export async function getProject(id) {
    if (!id) return null;
    const db = await openDB();
    try {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).get(id);
        let value = null;
        req.onsuccess = () => { value = req.result || null; };
        await awaitTx(tx);
        return value ? migrateProjectRecord(value) : null;
    } finally {
        db.close();
    }
}

/** List all projects, newest first. */
export async function listProjects() {
    const db = await openDB();
    try {
        const tx = db.transaction(STORE, 'readonly');
        const idx = tx.objectStore(STORE).index('updatedAt');
        const out = [];
        const cursor = idx.openCursor(null, 'prev');
        cursor.onsuccess = (e) => {
            const c = e.target.result;
            if (!c) return;
            out.push(migrateProjectRecord(c.value));
            c.continue();
        };
        await awaitTx(tx);
        return out;
    } finally {
        db.close();
    }
}

/** List all projects that reference a given file hash. Uses the v2
 * `fileHash` index — O(matches) instead of O(N) full-scan. */
export async function listProjectsForFile(fileHash) {
    if (!fileHash) return [];
    const db = await openDB();
    try {
        const tx = db.transaction(STORE, 'readonly');
        const idx = tx.objectStore(STORE).index('fileHash');
        const out = [];
        const cursor = idx.openCursor(IDBKeyRange.only(fileHash));
        cursor.onsuccess = (e) => {
            const c = e.target.result;
            if (!c) return;
            out.push(migrateProjectRecord(c.value));
            c.continue();
        };
        await awaitTx(tx);
        out.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        return out;
    } finally {
        db.close();
    }
}

/** Cascade-delete all projects that reference a given file hash. Single
 * transaction, tx.oncomplete barrier (Codex R2-3 + R2-4). */
export async function deleteProjectsForFile(fileHash) {
    if (!fileHash) return 0;
    const db = await openDB();
    try {
        const tx = db.transaction(STORE, 'readwrite');
        const idx = tx.objectStore(STORE).index('fileHash');
        let count = 0;
        const cursor = idx.openCursor(IDBKeyRange.only(fileHash));
        cursor.onsuccess = (e) => {
            const c = e.target.result;
            if (!c) return;
            c.delete();
            count++;
            c.continue();
        };
        await awaitTx(tx);
        return count;
    } finally {
        db.close();
    }
}

/** Delete a project by id. */
export async function deleteProject(id) {
    if (!id) return false;
    const db = await openDB();
    try {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(id);
        await awaitTx(tx);
        return true;
    } finally {
        db.close();
    }
}

/** Clear all projects. */
export async function clearProjects() {
    const db = await openDB();
    try {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).clear();
        await awaitTx(tx);
    } finally {
        db.close();
    }
}

/* ── migration ─────────────────────────────────────────────────────── */

/* Codex Round 2 follow-up H: lazy-migrate on read. When the schema
 * shape evolves (rename, drop, add-with-default), bump
 * PROJECT_SCHEMA_VERSION and add a clause here. Old records get patched
 * on first load; the next saveProject persists the new shape. */
function migrateProjectRecord(record) {
    if (!record) return record;
    /* Future: if (record.schemaVersion < 2) { ...migrate to v2... } */
    if (!record.schemaVersion) record.schemaVersion = 1;
    return record;
}
